import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { getOrgInfo } from "@/theme";

const isOwner = () => { try { const t=localStorage.getItem('token'); if(!t) return false; const p=JSON.parse(atob((t.split('.')[1]||''))); return !!p.is_owner; } catch { return false; } };
const tokenHasImp = () => { try { const t=localStorage.getItem('token'); if(!t) return false; const p=JSON.parse(atob((t.split('.')[1]||''))); return !!p.imp; } catch { return false; } };


const Nav = () => {
if(!isOwner()){ window.location.href='/campaigns'; }

  const org = getOrgInfo();
  const stopImpersonate = ()=>{ const backup=localStorage.getItem('owner_token_backup'); if(backup){ localStorage.setItem('token', backup); localStorage.removeItem('owner_token_backup'); window.location.href='/owner'; } else { localStorage.removeItem('token'); window.location.href='/owner'; } };
  return (
    <div className="flex items-center justify-between border-b pb-3" data-testid="top-nav-owner">
      <div className="flex items-center gap-3">
        {org?.logo_url && (<img src={org.logo_url} alt="logo" className="h-8" />)}
        <div className="font-semibold">{org?.name || 'Admin'}</div>
      </div>
      <div className="flex items-center gap-3">
        <a data-testid="nav-to-campaigns" className="underline" href="/campaigns">Campaigns</a>
        {isOwner() && (<a data-testid="nav-to-orgsusers" className="underline" href="/orgs-users">Orgs & Users</a>)}
        {isOwner() && (<a data-testid="nav-to-collections" className="underline" href="/collections">Leads</a>)}
        {tokenHasImp() && (<button data-testid="stop-impersonate" className="underline" onClick={stopImpersonate}>Stop impersonating</button>)}
        <button data-testid="nav-logout" onClick={()=>{ localStorage.removeItem('token'); window.location.href='/login'; }}>Logout</button>
      </div>
    </div>
  );
};

const PageHeader = () => (
  <div className="flex items-center justify-between mb-6">
    <div>
      <h1 className="text-2xl font-semibold text-[#171717]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="owner-header">MPs & Electorates</h1>
      <p className="text-sm text-gray-500">Manage the MP directory and electorate data.</p>
    </div>
  </div>
);

// Add MP Dialog Component
function AddMPDialog({ onAdd, mpLevel, mpHouse, mpState }) {
  const [open, setOpen] = useState(false);
  const [newMP, setNewMP] = useState({
    name: "",
    party: "",
    electorate: "",
    region: "",
    email: "",
    parliament_phone: "",
    electorate_phone: "",
    facebook: "",
    twitter: "",
    state: mpState || "",
    level: mpLevel || "federal",
    house: mpHouse || "lower"
  });

  const handleAdd = () => {
    if (!newMP.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!newMP.email.trim()) {
      toast.error("Email is required");
      return;
    }
    onAdd(newMP);
    setNewMP({
      name: "", party: "", electorate: "", region: "", email: "",
      parliament_phone: "", electorate_phone: "", facebook: "", twitter: "",
      state: mpState || "", level: mpLevel || "federal", house: mpHouse || "lower"
    });
    setOpen(false);
    toast.success("MP added! Click 'Save Changes' to persist.");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-mp-button" variant="default" size="sm">+ Add MP</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New MP</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input value={newMP.name} onChange={(e) => setNewMP({...newMP, name: e.target.value})} placeholder="Full name" />
            </div>
            <div>
              <label className="text-sm font-medium">Party</label>
              <Input value={newMP.party} onChange={(e) => setNewMP({...newMP, party: e.target.value})} placeholder="e.g., ALP, LIB" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Electorate</label>
              <Input value={newMP.electorate} onChange={(e) => setNewMP({...newMP, electorate: e.target.value})} placeholder="Electorate name" />
            </div>
            <div>
              <label className="text-sm font-medium">State</label>
              <select className="w-full border rounded px-3 py-2" value={newMP.state} onChange={(e) => setNewMP({...newMP, state: e.target.value})}>
                <option value="">Select State</option>
                <option value="ACT">ACT</option>
                <option value="NSW">NSW</option>
                <option value="NT">NT</option>
                <option value="QLD">QLD</option>
                <option value="SA">SA</option>
                <option value="TAS">TAS</option>
                <option value="VIC">VIC</option>
                <option value="WA">WA</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Email *</label>
            <Input value={newMP.email} onChange={(e) => setNewMP({...newMP, email: e.target.value})} placeholder="email@parliament.gov.au" type="email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Parliament Phone</label>
              <Input value={newMP.parliament_phone} onChange={(e) => setNewMP({...newMP, parliament_phone: e.target.value})} placeholder="(02) 6277 xxxx" />
            </div>
            <div>
              <label className="text-sm font-medium">Electorate Phone</label>
              <Input value={newMP.electorate_phone} onChange={(e) => setNewMP({...newMP, electorate_phone: e.target.value})} placeholder="(0x) xxxx xxxx" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Level</label>
              <select className="w-full border rounded px-3 py-2" value={newMP.level} onChange={(e) => setNewMP({...newMP, level: e.target.value})}>
                <option value="federal">Federal</option>
                <option value="state">State</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">House</label>
              <select className="w-full border rounded px-3 py-2" value={newMP.house} onChange={(e) => setNewMP({...newMP, house: e.target.value})}>
                <option value="lower">Lower House</option>
                <option value="upper">Upper House</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd}>Add MP</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Electorate Dialog Component
function AddElectorateDialog({ onAdd, elLevel, elState }) {
  const [open, setOpen] = useState(false);
  const [newEl, setNewEl] = useState({
    electorate: "",
    state: elState || "",
    postcodes: ""
  });

  const handleAdd = () => {
    if (!newEl.electorate.trim()) {
      toast.error("Electorate name is required");
      return;
    }
    if (!newEl.state) {
      toast.error("State is required");
      return;
    }
    onAdd({ ...newEl, level: elLevel });
    setNewEl({ electorate: "", state: elState || "", postcodes: "" });
    setOpen(false);
    toast.success("Electorate added! Click 'Save Changes' to persist.");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-electorate-button" variant="default" size="sm">+ Add Electorate</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Electorate</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          <div>
            <label className="text-sm font-medium">Electorate Name *</label>
            <Input value={newEl.electorate} onChange={(e) => setNewEl({...newEl, electorate: e.target.value})} placeholder="e.g., Sydney, Melbourne" />
          </div>
          <div>
            <label className="text-sm font-medium">State *</label>
            <select className="w-full border rounded px-3 py-2" value={newEl.state} onChange={(e) => setNewEl({...newEl, state: e.target.value})}>
              <option value="">Select State</option>
              <option value="ACT">ACT</option>
              <option value="NSW">NSW</option>
              <option value="NT">NT</option>
              <option value="QLD">QLD</option>
              <option value="SA">SA</option>
              <option value="TAS">TAS</option>
              <option value="VIC">VIC</option>
              <option value="WA">WA</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Postcodes</label>
            <Textarea 
              value={newEl.postcodes} 
              onChange={(e) => setNewEl({...newEl, postcodes: e.target.value})} 
              placeholder="Enter postcodes separated by semicolons (e.g., 2000; 2001; 2002)"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd}>Add Electorate</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DirectoryTable({ rows, onSave, onDelete, searchTerm = "" }){
  const [local, setLocal] = useState(rows);
  useEffect(()=>setLocal(rows),[rows]);
  const update = (i, key, val)=>{
    const copy=[...local];
    copy[i] = { ...copy[i], [key]: val };
    setLocal(copy);
  };
  const handleDelete = (i) => {
    const copy = [...local];
    copy.splice(i, 1);
    setLocal(copy);
    onDelete?.(copy);
  };
  
  const columns = ["name", "party", "electorate", "region", "email", "parliament_phone", "electorate_phone", "facebook", "twitter"];
  
  return (
    <div className="overflow-auto border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(h=> (
              <TableHead key={h} className="whitespace-nowrap">{h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</TableHead>
            ))}
            <TableHead className="w-16">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {local.map((r,i)=> (
            <TableRow key={r.id || i}>
              {columns.map((k)=> (
                <TableCell key={k}>
                  <Input data-testid={`mp-cell-${i}-${k}`} value={local[i][k]||""} onChange={(e)=>update(i,k,e.target.value)} className="min-w-[100px]" />
                </TableCell>
              ))}
              <TableCell>
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800" onClick={() => handleDelete(i)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="p-3 border-t flex justify-end">
        <Button data-testid="mp-save-button" onClick={()=>onSave(local)}>Save Changes</Button>
      </div>
    </div>
  );
}

export default function OwnerDashboard(){
  const [mps, setMps] = useState([]);
  const [electorates, setElectorates] = useState([]);
  const [elState, setElState] = useState("");
  const [elLevel, setElLevel] = useState("federal");
  const [elHouse, setElHouse] = useState("lower");
  const [elSearch, setElSearch] = useState("");
  const [mpSearch, setMpSearch] = useState("");
  const [mpLevel, setMpLevel] = useState("federal");
  const [mpHouse, setMpHouse] = useState("lower");
  const [mpState, setMpState] = useState("");

  const load = async ()=>{
    try{
      const [a,b] = await Promise.allSettled([
        api.get("/owner/mp-directory"),
        api.get(`/owner/electorates?level=${elLevel}`)
      ]);
      const m = a.status==='fulfilled' ? (a.value?.data?.rows||[]) : [];
      const e = b.status==='fulfilled' ? (b.value?.data?.rows||[]) : [];
      setMps(m); setElectorates(e);
    }catch(_){ setMps([]); setElectorates([]); }
  };
  useEffect(()=>{ load(); },[elLevel]);

  const saveMPs = async (rows)=>{
    try {
      await api.post("/owner/mp-directory/save", { rows });
      toast.success("Saved MPs");
      load();
    } catch (e) {
      toast.error("Failed to save MPs");
    }
  }
  
  const saveElectorates = async (rows)=>{
    try {
      await api.post("/owner/electorates/save", { rows, level: elLevel });
      toast.success("Saved Electorates");
      load();
    } catch (e) {
      toast.error("Failed to save electorates");
    }
  }
  
  // Add a new MP to the list (not saved until Save is clicked)
  const addMP = (newMP) => {
    const mp = {
      ...newMP,
      id: `new-${Date.now()}`,
      level: mpLevel,
      house: mpHouse,
      state: newMP.state || mpState
    };
    setMps([...mps, mp]);
  };
  
  // Add a new electorate to the list (not saved until Save is clicked)
  const addElectorate = (newEl) => {
    const el = {
      ...newEl,
      id: `new-${Date.now()}`,
      level: elLevel
    };
    setElectorates([...electorates, el]);
  };
  
  // Delete electorate from list
  const deleteElectorate = (index) => {
    const filtered = electorates
      .filter(r=> (elState? r.state === elState : true))
      .filter(r=> (elSearch? String(r.electorate||'').toLowerCase().includes(elSearch.toLowerCase()): true));
    const toDelete = filtered[index];
    if (toDelete) {
      setElectorates(electorates.filter(e => e.id !== toDelete.id));
    }
  };

  // Filter MPs based on current filters
  const filteredMPs = mps
    .filter(m => m.level === mpLevel)
    .filter(m => m.house === mpHouse)
    .filter(m => mpState ? m.state === mpState : true)
    .filter(m => mpSearch ? (m.name||"").toLowerCase().includes(mpSearch.toLowerCase()) || (m.electorate||"").toLowerCase().includes(mpSearch.toLowerCase()) : true);

  // Filter electorates based on current filters
  const filteredElectorates = electorates
    .filter(r => r.level === elLevel)
    .filter(r => (elState ? r.state === elState : true))
    .filter(r => (elSearch ? String(r.electorate||'').toLowerCase().includes(elSearch.toLowerCase()) : true));

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="owner-dashboard">
      <PageHeader />
      <Tabs defaultValue="mps">
        <TabsList>
          <TabsTrigger value="mps" data-testid="tab-mps">MP Directory</TabsTrigger>
          <TabsTrigger value="electorates" data-testid="tab-electorates">Electorates</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="mps">
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-col gap-3 w-full">
                <div className="flex items-center justify-between w-full">
                  <span>MP Directory</span>
                  <AddMPDialog onAdd={addMP} mpLevel={mpLevel} mpHouse={mpHouse} mpState={mpState} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select data-testid="mp-level" className="border rounded px-2 py-1 text-sm" value={mpLevel} onChange={e=>setMpLevel(e.target.value)}>
                    <option value="federal">Federal</option>
                    <option value="state">State</option>
                  </select>
                  <select data-testid="mp-house" className="border rounded px-2 py-1 text-sm" value={mpHouse} onChange={e=>setMpHouse(e.target.value)}>
                    <option value="lower">Lower House</option>
                    <option value="upper">Upper House</option>
                  </select>
                  <select data-testid="mp-state" className="border rounded px-2 py-1 text-sm" value={mpState} onChange={(e)=>setMpState(e.target.value)}>
                    <option value="">All States</option>
                    <option value="ACT">ACT</option>
                    <option value="NSW">NSW</option>
                    <option value="NT">NT</option>
                    <option value="QLD">QLD</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="VIC">VIC</option>
                    <option value="WA">WA</option>
                  </select>
                  <Input data-testid="mp-search" placeholder="Search MPs" className="w-48" onChange={(e)=>setMpSearch(e.target.value)} />
                  <span className="text-sm text-gray-500">({filteredMPs.length} MPs)</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DirectoryTable 
                rows={filteredMPs} 
                onSave={(rows) => {
                  // Update only the filtered rows in the full list
                  const otherMPs = mps.filter(m => 
                    m.level !== mpLevel || 
                    m.house !== mpHouse || 
                    (mpState && m.state !== mpState)
                  );
                  saveMPs([...otherMPs, ...rows]);
                }}
                onDelete={(rows) => {
                  const otherMPs = mps.filter(m => 
                    m.level !== mpLevel || 
                    m.house !== mpHouse || 
                    (mpState && m.state !== mpState)
                  );
                  setMps([...otherMPs, ...rows]);
                }}
                searchTerm={mpSearch} 
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="electorates">
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-col gap-3 w-full">
                <div className="flex items-center justify-between w-full">
                  <span>Electorates</span>
                  <AddElectorateDialog onAdd={addElectorate} elLevel={elLevel} elState={elState} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select data-testid="el-level" className="border rounded px-2 py-1" value={elLevel} onChange={e=>setElLevel(e.target.value)}>
                    <option value="federal">Federal (House of Reps)</option>
                    <option value="state">State (Lower House)</option>
                  </select>
                  <select data-testid="el-state" className="border rounded px-2 py-1" value={elState} onChange={(e)=>setElState(e.target.value)}>
                    <option value="">All States</option>
                    <option value="ACT">ACT</option>
                    <option value="NSW">NSW</option>
                    <option value="NT">NT</option>
                    <option value="QLD">QLD</option>
                    <option value="SA">SA</option>
                    <option value="TAS">TAS</option>
                    <option value="VIC">VIC</option>
                    <option value="WA">WA</option>
                  </select>
                  <Input data-testid="el-search" placeholder="Search electorates" value={elSearch} onChange={(e)=>setElSearch(e.target.value)} />
                  <span className="text-sm text-gray-500">({filteredElectorates.length} electorates)</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {["electorate","state","postcodes"].map(h=> (<TableHead key={h}>{h}</TableHead>))}
                      <TableHead className="w-16">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredElectorates.map((r,i)=> (
                      <TableRow key={r.id||i}>
                        <TableCell><Input data-testid={`el-cell-${i}-electorate`} value={r.electorate||""} onChange={(e)=>{ const c=[...electorates]; const idx = electorates.findIndex(el => el.id === r.id); if(idx >= 0) { c[idx]={...c[idx], electorate:e.target.value}; setElectorates(c); } }} /></TableCell>
                        <TableCell><Input data-testid={`el-cell-${i}-state`} value={r.state||""} onChange={(e)=>{ const c=[...electorates]; const idx = electorates.findIndex(el => el.id === r.id); if(idx >= 0) { c[idx]={...c[idx], state:e.target.value}; setElectorates(c); } }} /></TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <button className="text-left underline text-blue-600 hover:text-blue-800 cursor-pointer">
                                {(r.postcodes||"").split("; ").filter(p=>p.trim()).length} postcodes
                              </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>{r.state} - {r.electorate}</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4 space-y-4">
                                <div>
                                  <p className="text-sm text-gray-600 mb-2">Postcodes (semicolon-separated):</p>
                                  <Textarea 
                                    className="font-mono text-sm min-h-[200px]"
                                    value={r.postcodes || ""}
                                    onChange={(e) => {
                                      const c = [...electorates];
                                      const idx = electorates.findIndex(el => el.id === r.id);
                                      if(idx >= 0) {
                                        c[idx] = {...c[idx], postcodes: e.target.value};
                                        setElectorates(c);
                                      }
                                    }}
                                    placeholder="Enter postcodes separated by semicolons (e.g., 2000; 2001; 2002)"
                                  />
                                </div>
                                <div className="flex justify-end">
                                  <Button 
                                    onClick={() => {
                                      toast.success("Postcodes updated. Click 'Save Changes' at the bottom to persist.");
                                    }}
                                    size="sm"
                                  >
                                    Done
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800" onClick={() => deleteElectorate(i)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-3 border-t flex justify-end">
                  <Button data-testid="el-save-button" onClick={()=>saveElectorates(electorates)}>Save Changes</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="settings">
          <Card>
            <CardHeader><CardTitle>Platform Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 items-end">
                <div className="text-sm">OpenAI key status: <span id="oa-status" data-testid="openai-status">loading…</span></div>
              </div>
              <div className="flex items-center gap-2">
                <Input data-testid="openai-key" placeholder="sk-..." onChange={(e)=>window.__oa=e.target.value} />
                <Button data-testid="openai-save" onClick={async()=>{
                  try{ await api.post('/owner/settings', { openai_api_key: window.__oa||'' }); toast('Saved'); document.getElementById('oa-status').innerText='stored'; }
                  catch{ toast('Failed'); }
                }}>Save</Button>
                <Button variant="outline" data-testid="openai-check" onClick={async()=>{
                  try{ const r=await api.get('/owner/settings'); document.getElementById('oa-status').innerText = r?.data?.has_openai_key? 'stored':'missing'; }catch{ document.getElementById('oa-status').innerText='error'; }
                }}>Check</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
